/**
 * Better Left Alone — global theme behavior.
 * Native browser APIs only. No external dependencies.
 */

/** Header that gains a solid background after scrolling past the hero. */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.menuToggle = this.querySelector('[data-menu-toggle]');
    this.mobileNav = this.querySelector('[data-mobile-nav]');

    this.menuToggle?.addEventListener('click', () => this.#toggleMenu());

    this.#observeScroll();
  }

  #toggleMenu() {
    const isOpen = this.mobileNav?.getAttribute('data-open') === 'true';
    this.mobileNav?.setAttribute('data-open', String(!isOpen));
    this.menuToggle?.setAttribute('aria-expanded', String(!isOpen));
    document.body.style.overflow = isOpen ? '' : 'hidden';
  }

  #observeScroll() {
    // Belt-and-suspenders: if this page has no hero to sit transparently
    // over (no hero section, or a hero with no image uploaded), force the
    // solid/readable state immediately instead of trusting CSS alone to
    // detect that. Fixes bone-on-bone text when a hero has no image yet.
    const hero = document.querySelector('.hero');
    const heroHasImage = hero?.querySelector('.hero__media img');
    const alwaysSolid = !hero || !heroHasImage;

    const setScrolled = () => {
      this.classList.toggle('site-header--scrolled', alwaysSolid || window.scrollY > 24);
    };
    setScrolled();
    if (!alwaysSolid) {
      window.addEventListener('scroll', setScrolled, { passive: true });
    }
  }
}

customElements.define('site-header', SiteHeader);

/** Add-to-cart form: submits via fetch, opens the cart drawer, no page reload. */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[data-submit]');
    this.form?.addEventListener('submit', this.#handleSubmit.bind(this));
  }

  /** @param {SubmitEvent} event */
  async #handleSubmit(event) {
    event.preventDefault();
    if (!this.form) return;

    this.submitButton?.setAttribute('disabled', 'true');
    this.submitButton?.classList.add('is-loading');

    try {
      const formData = new FormData(this.form);
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || 'Could not add to cart');
      }

      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data, bubbles: true }));
      document.querySelector('cart-drawer')?.open();
    } catch (error) {
      console.error('[ProductForm]', error);
      this.dispatchEvent(new CustomEvent('cart:error', { detail: error, bubbles: true }));
    } finally {
      this.submitButton?.removeAttribute('disabled');
      this.submitButton?.classList.remove('is-loading');
    }
  }
}

customElements.define('product-form', ProductForm);

/** Slide-out cart drawer, refreshed via the Cart section-rendering API. */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.querySelector('[data-close-cart]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-cart-overlay]')?.addEventListener('click', () => this.close());
    document.addEventListener('cart:item-added', () => this.refresh());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }

  open() {
    this.setAttribute('data-open', 'true');
    document.body.style.overflow = 'hidden';
    this.refresh();
  }

  close() {
    this.setAttribute('data-open', 'false');
    document.body.style.overflow = '';
  }

  async refresh() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-drawer`);
      const data = await response.json();
      const currentContent = this.querySelector('[data-cart-drawer-content]');
      if (currentContent && data['cart-drawer']) currentContent.innerHTML = data['cart-drawer'];
      this.#bindLineItemControls();
    } catch (error) {
      console.error('[CartDrawer] refresh failed', error);
    }
  }

  #bindLineItemControls() {
    this.querySelectorAll('[data-quantity-change]').forEach((button) => {
      button.addEventListener('click', async () => {
        const line = Number(button.dataset.line);
        const delta = Number(button.dataset.quantityChange);
        const input = this.querySelector(`[data-quantity-input="${line}"]`);
        const newQty = Math.max(0, Number(input?.value || 0) + delta);
        await this.#updateLine(line, newQty);
      });
    });

    this.querySelectorAll('[data-remove-line]').forEach((button) => {
      button.addEventListener('click', async () => {
        await this.#updateLine(Number(button.dataset.removeLine), 0);
      });
    });
  }

  /**
   * @param {number} line
   * @param {number} quantity
   */
  async #updateLine(line, quantity) {
    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      await this.refresh();
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    } catch (error) {
      console.error('[CartDrawer] update failed', error);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

/** Any element that opens the cart drawer without a full navigation. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-open-cart]');
  if (!trigger) return;
  event.preventDefault();
  document.querySelector('cart-drawer')?.open();
});

/** Simple accordion enhancement (progressive — <details> works without this). */
class AccordionItem extends HTMLElement {
  connectedCallback() {
    const details = this.querySelector('details');
    details?.addEventListener('toggle', () => {
      if (!details.open) return;
      this.parentElement
        ?.querySelectorAll('accordion-item details[open]')
        .forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
    });
  }
}

customElements.define('accordion-item', AccordionItem);
